import { Component, SyntheticEvent, FC, useState, useMemo } from "react";
import { TabSatelliteProps } from "containers/satellites/SatellitesData";
import GroupComponent from "../group/GroupComponent";
import { Button } from "../button";
import { ComponentState, Item } from "types/Component";
import { convertValue, deleteManyEntity } from "utils/entities.utils";
import { fetchExtensionData, saveExtensions, createNewExtension, findAll } from "api";
import { AxiosError } from "axios";
import { Message } from "types/Message";
import { t } from "utils/i18n";
import { addMessage } from "actions/messages";
import TabHeader from "./TabHeader";
import { Pojo } from "types/Galaxy";
import { getColumnDefinition } from "api/column";
import { Row, Col } from "composants/Layout";
import SysDomaineAndLabel from "composants/select/SysDomaineAndLabel";
import { Fa } from "composants/Icon";
import { Trans } from "react-i18next";
import { tw } from "twind";
import { initRsqlCriteria } from "utils/query.utils";
import { RSQLFilterExpression, Operators } from "rsql-criteria-typescript";
import InteractiveReport from "containers/datatable/InteractiveReport";

export type ExtensionType = "A" | "N" | "D" | "C" | "S";
export interface ExtensionValue {
  id: number;
  columnName: string;
  dataType: string;
  value: any;
  unite: string;
  style: string;
}

export interface ExtensionsData {
  values: ExtensionValue[];
  composants: ComponentState[];
}

interface TabExtensionState {
  // Definition des composants à afficher
  definition: ComponentState[];
  extensionGSDefinition?: ComponentState;
  extensions: ExtensionValue[];
  isCreatingFromExisting: boolean;
  isDeleting: boolean;
  irSelectionExtension: Pojo | null;
  rowSelectedInIR: Pojo[] | null;
}

const DeleteBody: FC<{
  sjmoCode: string;
  extensions: ExtensionValue[];
  isDeleting: boolean;
  refresh: () => void;
  addMessage(message: Message): void;
}> = props => {
  const [selectedExtension, setSelectedExtension] = useState<string | null>(null);

  const sysDomaineChoices: Item[] = useMemo(() => {
    return props.extensions.map(ext => {
      return { label: ext.columnName, value: ext.id };
    });
  }, [props.extensions]);

  return (
    <Row>
      <Col span={9}>
        <SysDomaineAndLabel
          id="extensionId"
          value={selectedExtension}
          onChange={e => setSelectedExtension(convertValue(e))}
          sysDomaineChoices={sysDomaineChoices}
          label={t("commun_extensions")}
        />
      </Col>
      <Col span={3}>
        <Button
          onClick={() => {
            deleteManyEntity(
              "dataExtension",
              [selectedExtension as string],
              props.sjmoCode,
              () => props.refresh(),
              error => props.addMessage
            );
          }}
          disabled={!props.isDeleting || selectedExtension === undefined}
        >
          <span className="icon">
            <Fa icon="save" />
          </span>
          <span>
            <Trans i18nKey="commun_supprimer_extension" />
          </span>
        </Button>
      </Col>
    </Row>
  );
};

class TabExtension extends Component<TabSatelliteProps, TabExtensionState> {
  state: TabExtensionState = {
    extensions: [],
    isCreatingFromExisting: false,
    isDeleting: false,
    definition: [],
    irSelectionExtension: null,
    rowSelectedInIR: null
  };

  componentDidMount() {
    if (this.props.contextId) {
      this.refresh();
    }
    getColumnDefinition(this.props.sjmoCode, "dataExtension", ["extensionId"]).then(response => {
      this.setState({
        extensionGSDefinition: response.data[0] ? response.data[0] : undefined
      });
    });

    const rsql = initRsqlCriteria();
    rsql.filters.and(new RSQLFilterExpression("sjinCode", Operators.Equal, "SELECTION_EXTENSION"));
    findAll({
      tableName: "syjInteractiveReport",
      filter: rsql.build(),
      includeJoinParent: true
    }).then(res => {
      if (res.data && res.data.data.length > 0) {
        this.setState({ irSelectionExtension: res.data.data[0] });
      }
    });
  }

  componentDidUpdate(prevProps: TabSatelliteProps) {
    if (prevProps.contextId !== this.props.contextId && this.props.contextId) {
      this.refresh();
    }
  }

  refresh = () => {
    fetchExtensionData(this.props.tableName, this.props.contextId as string)
      .then(response => {
        let metaData = {};
        response.data[0].values.forEach((v: ExtensionValue) => {
          metaData[v.columnName] = v;
        });

        this.setState({
          definition: response.data[0].composants,
          extensions: response.data[0].values
        });
      })
      .catch(e => {
        const er = e as AxiosError<any>;
        if (!er.response) {
          return;
        }

        const message: Message = {
          code: er.response.data.code,
          message: t(er.response.data.message),
          type: er.response.data.type,
          target: er.response.data.target
        };
        addMessage(message);
      });

    this.props.countAction(this.props.tableName, this.props.contextId);
    this.setState({
      isCreatingFromExisting: false,
      isDeleting: false
    });
  };

  render() {
    let extensionValues: any = {};
    let _style = {};
    this.state.extensions.forEach((v: ExtensionValue) => {
      extensionValues[v.columnName] = v.value;
      if (v.style) {
        _style[v.columnName] = v.style;
      }
    });
    extensionValues._style = _style;

    const newBody =
      this.state.isCreatingFromExisting && this.state.irSelectionExtension?.id ? (
        <div className={tw("h-96")}>
          <InteractiveReport
            sjmoCode={this.props.sjmoCode}
            definitionId={this.state.irSelectionExtension?.id ?? ""}
            isAdmin={false}
            isProcessusActive={false}
            onAfterSaveDatatable={() => this.refresh()}
            onChangeSelectedEntities={(e: Pojo[]) => {
              this.setState({ rowSelectedInIR: e });
            }}
          />
        </div>
      ) : null;
    const deleteBody = this.state.isDeleting ? (
      <DeleteBody
        sjmoCode={this.props.sjmoCode}
        extensions={this.state.extensions}
        refresh={this.refresh}
        isDeleting={this.state.isDeleting}
        addMessage={this.props.addMessage}
      />
    ) : null;

    return (
      <>
        <TabHeader
          i18nKey="commun_extensions_liees"
          tableName={this.props.tableName}
          contextId={this.props.contextId}
          count={this.props.count}
          sjmoCode={this.props.sjmoCode}
        >
          <Button
            className="button is-success is-rounded mr-6"
            onClick={() => this.save()}
            disabled={this.props.contextId === undefined}
            title={t("commun_valider")}
          >
            <span className="icon">
              <Fa icon="save" className="mr-7" />
            </span>
            <span>
              <Trans i18nKey="commun_valider" />
            </span>
          </Button>
          <Button
            className="button is-link is-rounded mr-6"
            onClick={() =>
              this.setState({ isCreatingFromExisting: !this.state.isCreatingFromExisting })
            }
            disabled={this.props.contextId === undefined}
          >
            <span className="icon">
              <Fa icon="plus" />
            </span>
            <span>
              {this.state.isCreatingFromExisting ? (
                <Trans i18nKey="commun_cacher" />
              ) : (
                <Trans i18nKey="commun_ajouter" />
              )}
            </span>
          </Button>
          <Button
            className="button is-link is-rounded mr-6"
            onClick={() => this.setState({ isDeleting: !this.state.isDeleting })}
            disabled={this.props.contextId === undefined}
          >
            <span className="icon">
              <Fa icon="trash" />
            </span>
            <span>
              <Trans i18nKey="commun_supprimer" />
            </span>
          </Button>
        </TabHeader>
        {newBody}
        {deleteBody}
        <>
          <GroupComponent
            group={{
              groupLabel: "",
              groupSize: 12,
              compos: this.state.definition,
              position: 0,
              datatableTableName: "",
              datatableCtrlKey: ""
            }}
            groupSize={12}
            entity={extensionValues}
            wviState={{}}
            onChange={this.onChange}
            onValueChange={this.onValueChange}
            onItemChange={this.onItemChange}
            onBlur={this.onBlur}
            contextMenu={() => {
              /* rien */
            }}
            sjmoCode={this.props.sjmoCode}
          />
        </>
      </>
    );
  }

  onChange = (e: SyntheticEvent<any>) => {
    const field: string = e.currentTarget.name;
    const value = convertValue(e);

    let extensions = [...this.state.extensions];
    extensions = extensions.map(ext => {
      let newValue = value;
      if (ext.dataType === "C") {
        newValue = value === true ? "O" : "N";
      }

      if (ext.columnName === field) {
        return { ...ext, value: newValue };
      } else {
        return ext;
      }
    });

    this.setState({
      extensions
    });
  };

  onValueChange = (field: string | undefined, value: any) => {
    let extensions = [...this.state.extensions];
    extensions = extensions.map(ext => {
      let newValue = value;
      if (ext.dataType === "C") {
        newValue = value === true ? "O" : "N";
      }

      if (ext.columnName === field) {
        return { ...ext, value: newValue };
      } else {
        return ext;
      }
    });

    this.setState({
      extensions
    });
  };

  onItemChange = (pojo: Pojo, name: string) => {
    let extensions = [...this.state.extensions];
    extensions = extensions.map(ext => {
      let newValue = pojo?.id;

      if (ext.columnName === name) {
        return { ...ext, value: newValue };
      } else {
        return ext;
      }
    });

    this.setState({
      extensions
    });
  };

  onBlur = (e: SyntheticEvent<any>) => {
    // pas de WVI sur les extensions
  };

  save = async () => {
    let metaData = {};
    this.state.extensions.forEach((v: ExtensionValue) => {
      metaData[v.columnName] = v;
    });
    if (this.state.rowSelectedInIR) {
      for (let itemSelectedExtension of this.state.rowSelectedInIR) {
        await createNewExtension({
          sjmoCode: this.props.sjmoCode,
          tableName: this.props.tableName,
          contextId: this.props.contextId as string,
          fromExisting: true,
          data: {
            extensionId: itemSelectedExtension.extensionId
          }
        }).then(response => {
          this.setState({ isCreatingFromExisting: false, rowSelectedInIR: null });
        });
      }
    }
    saveExtensions(
      this.props.sjmoCode,
      this.props.tableName,
      this.props.contextId as string,
      metaData
    )
      .then(response => {
        this.setState({
          definition: response.data[0].composants,
          extensions: response.data[0].values
        });

        this.props.countAction(this.props.tableName, this.props.contextId as string);
      })
      .catch(e => {
        const er = e as AxiosError<any>;
        if (!er.response) {
          return;
        }

        const message: Message = {
          code: er.response.data.code,
          message: t(er.response.data.message),
          type: er.response.data.type,
          target: er.response.data.target
        };
        addMessage(message);
      });
  };
}

export default TabExtension;
